import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Phase-1 starter set: 4 cadences + 4 modal signatures + 4 progressions.
 * Copy follows the four-register convention:
 *   - beginner:     plain English, no Roman numerals.
 *   - intermediate: Roman numerals + plain function name.
 *   - advanced:     full theory vocabulary.
 *   - off (handled at use site by returning null — no copy lives here).
 *
 * Cadence/PAC nuance: v1 does not distinguish PAC from IAC (no soprano data),
 * so `cadence-authentic` describes the imperfect-authentic case and does not
 * promise PAC. See spec for the analyzer constraint.
 *
 * Symbol conventions:
 *   - Chord shorthand: `Cmaj7` (never `CM7` or `C△7`).
 *   - Roman numerals: uppercase major-quality, lowercase minor-quality.
 *   - Flat/sharp degrees use Unicode (`♭VII`, `♯IV`) to match analyzer output.
 *
 * Drafts are by Les; Bowie reviews for music-correctness + register polish.
 */
export const LEXICON_ENTRIES: LexiconEntry[] = [
  // ───────── Cadences (4) ─────────
  {
    id: 'cadence-authentic',
    category: 'cadence',
    displayName: 'Authentic cadence',
    displayNameShort: 'Authentic',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The strongest landing — tension resolves home.',
      intermediate: 'V → I — the dominant resolves to the tonic.',
      advanced: 'V → I authentic cadence (imperfect — soprano voice not tracked).',
    },
  },
  {
    id: 'cadence-plagal',
    category: 'cadence',
    displayName: 'Plagal cadence',
    displayNameShort: 'Plagal',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A gentle landing — the "amen" feel.',
      intermediate: 'IV → I — the subdominant resolves to the tonic.',
      advanced: 'IV → I plagal cadence (the "amen" cadence).',
    },
    genreTags: ['hymn', 'gospel'],
  },
  {
    id: 'cadence-half',
    category: 'cadence',
    displayName: 'Half cadence',
    displayNameShort: 'Half',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'An open landing — leaves the listener waiting.',
      intermediate: 'Ends on V — unresolved; sets up the next phrase.',
      advanced: 'Phrase ending on V — half (open) cadence.',
    },
  },
  {
    id: 'cadence-deceptive',
    category: 'cadence',
    displayName: 'Deceptive cadence',
    displayNameShort: 'Deceptive',
    pedagogicalLevel: 2,
    copy: {
      beginner: "A surprise twist — the chord doesn't land where you expect.",
      intermediate: 'V → vi — the dominant sidesteps to vi instead of I.',
      advanced: 'V → vi deceptive cadence (V → VI in minor; spelled VI since the chord is diatonic to natural minor).',
    },
  },

  // ───────── Modal signatures (4) ─────────
  {
    id: 'mode-dorian-signature',
    category: 'mode',
    displayName: "Dorian's natural 6",
    displayNameShort: 'Dorian ♮6',
    pedagogicalLevel: 2,
    copy: {
      beginner: "That bittersweet color — Dorian's signature.",
      intermediate: "Dorian's ♮6 — minor with a brighter sixth.",
      advanced: "Dorian's characteristic ♮6 — distinguishes Dorian from natural minor (jazz/folk).",
    },
    genreTags: ['jazz', 'folk', 'modal'],
  },
  {
    id: 'mode-mixolydian-signature',
    category: 'mode',
    displayName: "Mixolydian's flat 7",
    displayNameShort: 'Mixolydian ♭7',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Rock and bluesy — Mixolydian's signature.",
      intermediate: "Mixolydian's ♭7 — major with a lowered seventh.",
      advanced: "Mixolydian's characteristic ♭7 — major scale with a lowered leading tone (rock/blues).",
    },
    genreTags: ['rock', 'blues', 'modal'],
  },
  {
    id: 'mode-phrygian-signature',
    category: 'mode',
    displayName: "Phrygian's flat 2",
    displayNameShort: 'Phrygian ♭2',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Dark, Spanish color — Phrygian's signature.",
      intermediate: "Phrygian's ♭2 — minor with a lowered second.",
      advanced: "Phrygian's characteristic ♭2 — minor scale with a lowered second (Spanish/dark).",
    },
    genreTags: ['flamenco', 'metal', 'modal'],
  },
  {
    id: 'mode-lydian-signature',
    category: 'mode',
    displayName: "Lydian's sharp 4",
    displayNameShort: 'Lydian ♯4',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Dreamy, floating — Lydian's signature.",
      intermediate: "Lydian's ♯4 — major with a raised fourth.",
      advanced: "Lydian's characteristic ♯4 — major scale with a raised fourth (film/jazz).",
    },
    genreTags: ['film', 'jazz', 'modal'],
  },

  // ───────── Progressions (4) ─────────
  {
    id: 'progression-I-V-vi-IV',
    category: 'progression',
    displayName: 'Four-chord pop loop',
    displayNameShort: 'I–V–vi–IV',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The four-chord pop loop — used in countless hits.',
      intermediate: 'I–V–vi–IV — the "Axis of Awesome" loop.',
      advanced: 'I–V–vi–IV cyclical loop; tonic, dominant, submediant, subdominant.',
    },
    genreTags: ['pop'],
  },
  {
    id: 'progression-vi-IV-I-V',
    category: 'progression',
    displayName: 'Minor-start pop loop',
    displayNameShort: 'vi–IV–I–V',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A bittersweet pop loop — starts on the minor sixth.',
      intermediate: 'vi–IV–I–V — relative-minor-anchored pop loop.',
      advanced: 'vi–IV–I–V — axis rotation; historically the "sensitive-female" progression.',
    },
    genreTags: ['pop'],
  },
  {
    id: 'progression-I-vi-IV-V',
    category: 'progression',
    displayName: '50s doo-wop loop',
    displayNameShort: 'I–vi–IV–V',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The doo-wop loop — classic 50s ballad sound.',
      intermediate: 'I–vi–IV–V — the doo-wop / "Stand By Me" rotation.',
      advanced: 'I–vi–IV–V — 50s-progression / "ice cream changes".',
    },
    genreTags: ['pop', 'doowop'],
  },
  {
    id: 'progression-ii-V-I',
    category: 'progression',
    displayName: 'Jazz turnaround',
    displayNameShort: 'ii–V–I',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The classic jazz turnaround — tension resolves home.',
      intermediate: 'ii–V–I — predominant, dominant, tonic.',
      advanced: 'ii–V–I — the canonical jazz cadential progression.',
    },
    genreTags: ['jazz'],
  },
]
